import { useMemo, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, Image as ImageIcon, Trees, Flower2, Mountain, Home, BedDouble, Bath, ChefHat, Armchair, Footprints, Sunset, UtensilsCrossed, UserRound } from "lucide-react";
import Placeholder from "./Placeholder";
import { fullImageUrl } from "@/lib/images";

const PLACEHOLDER_SLOTS = [
  { label: "Exterior of ChiMano House", cat: "Property", icon: Home },
  { label: "Front garden", cat: "Gardens", icon: Flower2 },
  { label: "Orchard and fruit trees", cat: "Gardens", icon: Trees },
  { label: "Flowering plants", cat: "Gardens", icon: Flower2 },
  { label: "Open lawn and compound", cat: "Property", icon: Mountain },
  { label: "Living room", cat: "Interiors", icon: Armchair },
  { label: "Bedrooms", cat: "Interiors", icon: BedDouble },
  { label: "Bathrooms", cat: "Interiors", icon: Bath },
  { label: "Kitchen and dining area", cat: "Interiors", icon: ChefHat },
  { label: "Outdoor seating", cat: "Property", icon: Armchair },
  { label: "Pathways around the property", cat: "Property", icon: Footprints },
  { label: "Evening view", cat: "Property", icon: Sunset },
  { label: "Home-cooked meals", cat: "Care", icon: UtensilsCrossed },
  { label: "Caretaker-assisted services", cat: "Care", icon: UserRound },
];

const CATEGORIES = ["All", "Property", "Gardens", "Interiors", "Care"];

export default function Gallery({ images = [] }) {
  const [filter, setFilter] = useState("All");
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Build unified item list: real uploaded gallery images first, fall back to placeholders.
  const items = useMemo(() => {
    const real = images.map((img) => ({
      kind: "real",
      label: img.caption || "ChiMano House",
      caption: img.caption || "",
      alt: img.alt || img.caption || "ChiMano House",
      cat: img.category || "Property",
      url: fullImageUrl(img),
      id: img.id,
    }));
    if (real.length > 0) return real;
    return PLACEHOLDER_SLOTS.map((s, i) => ({
      kind: "placeholder",
      label: s.label,
      caption: s.cat,
      cat: s.cat,
      icon: s.icon,
      id: `ph-${i}`,
    }));
  }, [images]);

  const filtered = useMemo(
    () => (filter === "All" ? items : items.filter((i) => i.cat === filter)),
    [items, filter]
  );

  const close = useCallback(() => setLightboxIdx(null), []);
  const prev = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length)),
    [filtered.length]
  );
  const next = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i + 1) % filtered.length)),
    [filtered.length]
  );

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, close, prev, next]);

  const layouts = ["col-span-5 row-span-2", "col-span-4", "col-span-3", "col-span-4", "col-span-4", "col-span-4"];

  return (
    <section id="gallery" className="py-24 md:py-32 bg-[#F7F5F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 reveal">
          <div className="max-w-xl">
            <div className="text-[11px] uppercase tracking-[0.35em] text-[#985F47] mb-5">Photo gallery</div>
            <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tightest text-[#2C392F] font-light">
              A first look around the <span className="italic text-[#985F47]">house.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`gallery-filter-${c.toLowerCase()}`}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-[12px] tracking-[0.18em] uppercase transition-all border ${
                  filter === c
                    ? "bg-[#2C392F] text-[#F7F5F0] border-[#2C392F]"
                    : "bg-transparent text-[#2C392F] border-[#2C392F]/30 hover:border-[#2C392F]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Desktop masonry-ish grid */}
        <div className="hidden md:grid grid-cols-12 gap-5 auto-rows-[minmax(180px,auto)]">
          {filtered.map((img, idx) => {
            const span = layouts[idx % layouts.length];
            return (
              <button
                key={img.id}
                data-testid={`gallery-item-${idx}`}
                onClick={() => setLightboxIdx(idx)}
                className={`${span} text-left group`}
              >
                {img.kind === "real" ? (
                  <RealTile img={img} index={idx + 1} />
                ) : (
                  <Placeholder
                    label={img.label}
                    caption={img.cat}
                    icon={img.icon || ImageIcon}
                    ratio="h-full min-h-[200px]"
                    index={idx + 1}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Mobile carousel */}
        <div className="md:hidden -mx-6 px-6 overflow-x-auto flex gap-4 snap-x snap-mandatory pb-2">
          {filtered.map((img, idx) => (
            <button
              key={`m-${img.id}`}
              data-testid={`gallery-mobile-item-${idx}`}
              onClick={() => setLightboxIdx(idx)}
              className="snap-start flex-shrink-0 w-[78vw] text-left"
            >
              {img.kind === "real" ? (
                <RealTile img={img} ratio="aspect-[4/5]" index={idx + 1} />
              ) : (
                <Placeholder
                  label={img.label}
                  caption={img.cat}
                  icon={img.icon || ImageIcon}
                  ratio="aspect-[4/5]"
                  index={idx + 1}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && filtered[lightboxIdx] && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[80] bg-[#1A1C1A]/95 backdrop-blur-md flex items-center justify-center p-6"
          onClick={close}
        >
          <button
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 text-[#F7F5F0]/80 hover:text-[#F7F5F0]"
            onClick={(e) => { e.stopPropagation(); close(); }}
          >
            <X size={28} />
          </button>
          <button
            data-testid="gallery-lightbox-prev"
            className="absolute left-4 md:left-10 text-[#F7F5F0]/80 hover:text-[#F7F5F0] p-3"
            onClick={(e) => { e.stopPropagation(); prev(); }}
          >
            <ChevronLeft size={36} />
          </button>
          <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            {filtered[lightboxIdx].kind === "real" ? (
              <figure className="relative bg-black rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src={filtered[lightboxIdx].url}
                  alt={filtered[lightboxIdx].alt}
                  className="absolute inset-0 h-full w-full object-contain"
                />
              </figure>
            ) : (
              <Placeholder
                label={filtered[lightboxIdx].label}
                caption={filtered[lightboxIdx].cat}
                icon={filtered[lightboxIdx].icon || ImageIcon}
                ratio="aspect-[4/3]"
                tone="dark"
                index={lightboxIdx + 1}
              />
            )}
            <div className="mt-4 text-center text-[#F7F5F0]/60 text-sm">
              {lightboxIdx + 1} / {filtered.length} · {filtered[lightboxIdx].label}
            </div>
          </div>
          <button
            data-testid="gallery-lightbox-next"
            className="absolute right-4 md:right-10 text-[#F7F5F0]/80 hover:text-[#F7F5F0] p-3"
            onClick={(e) => { e.stopPropagation(); next(); }}
          >
            <ChevronRight size={36} />
          </button>
        </div>
      )}
    </section>
  );
}

function RealTile({ img, ratio = "h-full min-h-[200px]", index }) {
  return (
    <figure className={`relative overflow-hidden rounded-2xl ${ratio} bg-[#EFEAE4] border border-[#2C392F]/10 group`}>
      <img
        src={img.url}
        alt={img.alt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.04]"
      />
      <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] bg-[#F7F5F0]/85 text-[#2C392F] px-2 py-0.5 rounded-full">
        {img.cat}
      </span>
      {typeof index === "number" && (
        <span className="absolute top-3 right-3 serif text-xs tracking-[0.3em] bg-[#1A1C1A]/45 text-[#F7F5F0] px-2 py-0.5 rounded-full">
          № {String(index).padStart(2, "0")}
        </span>
      )}
      {img.caption && (
        <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#1A1C1A]/70 via-[#1A1C1A]/15 to-transparent px-4 py-3">
          <span className="serif italic text-[#F7F5F0] text-base leading-tight">{img.caption}</span>
        </figcaption>
      )}
    </figure>
  );
}
